import { defineComponent as _defineComponent } from 'vue'
import { BModal } from 'bootstrap-vue';
import { makeFormModel, getChildModel } from 'ah-common-lib/src/form/helpers';
import { selectField, radioField } from 'ah-common-lib/src/form/models';
import { subYears, startOfYear, isSameMonth } from 'date-fns';
import { FormEvent, FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { setState } from 'ah-common-lib/src/form/helpers';
import { helpers, required } from '@vuelidate/validators';
import { Client } from 'ah-api-gateways/models';
import { cloneDeep } from 'lodash';
import { FieldOptionObj } from 'ah-common-lib/src/form/interfaces';
import { reactive, ref, computed, watch } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useTradeState } from '../..';


export default /*#__PURE__*/_defineComponent({
  __name: 'DownloadEOMStatementModal',
  props: {
    client: null
  },
  setup(__props: any) {

const props = __props as {
  client: Client;
};

const tradeState = useTradeState();
const requestManager = useRequestManager().manager;

const monthsList = [
  { value: 0, label: 'January' },
  { value: 1, label: 'February' },
  { value: 2, label: 'March' },
  { value: 3, label: 'April' },
  { value: 4, label: 'May' },
  { value: 5, label: 'June' },
  { value: 6, label: 'July' },
  { value: 7, label: 'August' },
  { value: 8, label: 'September' },
  { value: 9, label: 'October' },
  { value: 10, label: 'November' },
  { value: 11, label: 'December' },
];

const downloadEomStatementFormModal = ref<BModal | null>(null);



const state = reactive({
  startDate: startOfYear(subYears(new Date(), 5)) as Date,
  years: [] as FieldOptionObj[],
  months: [] as FieldOptionObj[],
});

const optionForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'optionForm',
    fieldType: 'form',
    fields: [
      radioField(
        'isMonthly',
        '',
        [
          { value: false, label: 'Most recent' },
          { value: true, label: 'Select month' },
        ],
        {
          required: true,
          defaultValue: false,
          inline: true,
        }
      ),
    ],
  }),
  validation: null,
});

const dateForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'dateForm',
    fieldType: 'form',
    fields: [
      selectField('year', '', [], {
        required: () => optionForm.form.isMonthly,
        placeholder: 'Year',
        fieldWrapperClass: 'col col-5',
      }),
      selectField(
        'month',
        '',
        monthsList,
        {
          required: () => optionForm.form.isMonthly,
          placeholder: 'Month',
          fieldWrapperClass: 'col col-5',
          errorMessages: {
            minMonth: 'Please select a month up to the last month',
            required: 'Required field',
          },
        },
        {
          minMonth: helpers.withParams({ type: 'minMonth' }, (value: number, vm: any) => {
            const lastAvailable = getLastAvailableMonth();
            if (vm.year === lastAvailable.year) {
              return vm.month <= lastAvailable.month;
            }
            return vm.year <= lastAvailable.year;
          }),
          required: required,
        }
      ),
    ],
  }),
  validation: null,
});

const clientHasData = computed(() => !isSameMonth(state.startDate, new Date()));

const clientId = computed(() => props.client.id);

function clearErrorMessages() {
  dateForm.validation?.$reset();
}

function showModal() {
  downloadEomStatementFormModal.value?.show();
}

function cancel() {
  downloadEomStatementFormModal.value?.hide();
}

function send() {
  let date = getLastAvailableMonth();
  if (optionForm.form.isMonthly === true) {
    if (dateForm.validation?.$invalid) {
      dateForm.validation?.$touch();
      return;
    }
    date.year = dateForm.form!.year;
    date.month = dateForm.form!.month;
  }

  return requestManager
    .cancelAndNew(
      `exportingStatement`,
      tradeState.services.tradeReports.downloadEOMStatement({
        eomStatementMonth: date.month + 1,
        eomStatementYear: date.year,
        clientId: clientId.value,
      })
    )
    .subscribe((doc) => tradeState.store.useNotificationsStore().triggerFileExportRequestNotification(doc));
}

function setFieldsState() {
  setState(getChildModel(dateForm.form!, 'year')!, 'readonly', !optionForm.form.isMonthly);
  setState(getChildModel(dateForm.form!, 'month')!, 'readonly', !dateForm.form!.year || !optionForm.form.isMonthly);
}

function calculateMonthsOptions() {
  const list = cloneDeep(monthsList);
  const isCurrentYear = new Date().getFullYear() === dateForm.form!.year;

  if (state.startDate.getFullYear() === dateForm.form!.year) {
    const end = isCurrentYear ? new Date().getMonth() : list.length;
    state.months = list.slice(state.startDate.getMonth(), end);
  } else if (isCurrentYear) {
    state.months = list.slice(0, new Date().getMonth());
  } else {
    state.months = list;
  }
  setState(getChildModel(dateForm.form!, 'month')!, 'options', state.months);
}

function selectedDate(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    optionForm.form.isMonthly = true;
    if (event.model.$name === 'year') {
      dateForm.form!.month = '';
      setFieldsState();
      clearErrorMessages();
    }
    calculateMonthsOptions();
  }
}

function onFormChange() {
  if (!dateForm.form) {
    return;
  }
  if (optionForm.form.isMonthly) {
    dateForm.form.year = state.years.length === 1 ? state.years[0].value : '';
    calculateMonthsOptions();
    dateForm.form.month = state.months.length === 1 ? state.months[0].value : '';
  } else {
    const lastDates = getLastAvailableMonth();
    dateForm.form.year = lastDates.year;
    dateForm.form.month = lastDates.month;
  }
  setFieldsState();
  clearErrorMessages();
}

watch(
  () => optionForm.form.isMonthly,
  () => {
    onFormChange();
  },
  { immediate: true }
);

function getLastAvailableMonth() {
  let year = new Date().getFullYear();
  let month = new Date().getUTCMonth() - 1;
  if (month < 0) {
    year = year - 1;
    month = 11;
  }

  return { year, month };
}

function onClientChange() {
  if (props.client.createdAt) {
    state.startDate = new Date(props.client.createdAt);
  }

  let currentYear = getLastAvailableMonth().year;

  while (currentYear >= state.startDate.getFullYear()) {
    state.years.push({ value: currentYear, label: currentYear.toString() });
    currentYear -= 1;
  }

  if (dateForm.form) setState(getChildModel(dateForm.form, 'year')!, 'options', state.years);
}

watch(
  () => props.client,
  () => {
    onClientChange();
  },
  { immediate: true }
);

return { __sfc: true,tradeState, requestManager, monthsList, downloadEomStatementFormModal, props, state, optionForm, dateForm, clientHasData, clientId, clearErrorMessages, showModal, cancel, send, setFieldsState, calculateMonthsOptions, selectedDate, onFormChange, getLastAvailableMonth, onClientChange, BModal }
}

})